import React, { useEffect, useMemo, useState } from "react";
import { CategoryType } from "../../../types/category.type";
import { HEADER_HEIGHT } from "../../../utils/ui.constants";
import { useAlertContext } from "../../../contexts/alert/AlertProvider";
import { useSelector } from "react-redux";
import { TableColumn } from "react-data-table-component";
import { Box, Stack, Tooltip, Typography, Zoom } from "@mui/material";
import { asyncGetList } from "../../../utils/httpRequests";
import TableApp from "../../../components/common/tableapp/TableApp";
import Tablepagination from "../../../components/common/tableapp/TablePagination";
import { cloneDeep } from "lodash";

interface ITrashBase {
  category: CategoryType;
}

const heights = {
  header: HEADER_HEIGHT,
  py: "32px",
  topList: "50px",
  pagination: "42px",
};
const widthes = {
  filter: "350px",
};

function TrashBase({ category }: ITrashBase) {
  const { showAlert } = useAlertContext();
  const token = useSelector((state: any) => state.auth.token);
  const [load, setLoad] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [clickedData, setClickedData] = useState<any>();
  const [toggleClear, setToggleClear] = useState<boolean>(false);
  const [expandFilter, setExpandFilter] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [openModalForm, setOpenModalForm] = useState<boolean>(false);
  const [condition, setCondition] = useState<
    { [key: string]: any } | undefined
  >({});
  const [paginationOption, setPaginationOption] = useState<{
    page: number;
    limit: number;
    totalRows: number;
  }>({
    limit: 20,
    page: 1,
    totalRows: 0,
  });

  // row per page change
  const handleRowPerPageChange = (value: number) => {
    setPaginationOption({ ...paginationOption, page: 1, limit: value });
  };

  const handleRowClicked = (row: any) => {
    if (category.Form) {
      setClickedData(row);
      setOpenModalForm(true);
    }
  };

  const handleCloseModalForm = () => {
    setClickedData(null);
    setOpenModalForm(false);
  };

  const getListData = async () => {
    try {
      if (loading) return;
      setLoading(true);
      let query: any = { ...condition };

      const resp = await asyncGetList({
        apiCode: category.apiCode,
        token,
        extraPath: "/g/log/DELETE",
        condition: {
          t: 1,
          page: paginationOption.page,
          limit: paginationOption.limit,
          q: query,
        },
      });

      if (resp?.status === 200) {
        const result = (resp?.data || []).map((item: any) => item.data.data);
        setData(result);
        setPaginationOption({
          ...paginationOption,
          totalRows: result.length || 0,
        });
        setLoading(false);
      }
    } catch (error: any) {
      showAlert({
        type: "error",
        message:
          error?.data?.message || error?.data?.error || "Lỗi khi tải dữ liệu",
      });
    } finally {
      setToggleClear(!toggleClear);
    }
  };

  const columns: TableColumn<any>[] = useMemo(() => {
    return (category.columns || []).map((col: TableColumn<any>) => {
      if (!col.cell) {
        return {
          ...col,
          cell: (row, index) => {
            return (
              <Tooltip
                placement="right"
                arrow
                TransitionComponent={Zoom}
                title={`${
                  col.format ? col.format(row, index) : col.selector?.(row)
                }`}
              >
                <div data-tag="allowRowEvents" className="two-lines-ellipsis">
                  {col.format ? col.format(row, index) : col.selector?.(row)}
                </div>
              </Tooltip>
            );
          },
        };
      }
      return col;
    });
  }, [category.columns]);

  const dataToShow: any[] = useMemo(() => {
    let result: any[] = [];
    const page = paginationOption.page;
    const limit = paginationOption.limit;
    const dataClone = cloneDeep(data);

    const skip = (page - 1) * limit;
    result = dataClone.slice(skip, skip + limit);

    return result;
  }, [data, paginationOption.limit, paginationOption.page]);

  useEffect(() => {
    if (category.apiCode) {
      getListData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => {
    if (category.apiCode) {
      setCondition([]);
      setPaginationOption({ limit: 20, page: 1, totalRows: 0 });
      setLoad(load + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.apiCode]);

  useEffect(() => {
    setPaginationOption({ limit: 20, page: 1, totalRows: 0 });
    setLoad(load + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  return (
    <>
      {/* Form  */}
      {category?.Form && openModalForm && (
        <category.Form
          apiCode={category?.apiCode}
          isEdit={false}
          defaultValues={clickedData}
          open={openModalForm}
          onClose={handleCloseModalForm}
          setLoad={setLoad}
          title="Khôi phục"
          freeMode
        />
      )}
      {/* Form end */}
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          backgroundColor: "background.paper",
          borderRadius: "10px",
        }}
      >
        {/* Top */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexShrink: 0,
            height: heights.topList,
            px: 2,
            borderBottom: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography
            sx={{ fontSize: "20px", lineHeight: "20px", fontWeight: 600 }}
          >
            Thùng rác - {category?.title}
          </Typography>
          {/* <Stack direction="row" alignItems="center" gap={1}>
            {category.allowFilter && (
              <IconButton
                onClick={() => setExpandFilter(!expandFilter)}
                sx={{
                  borderRadius: "10px",
                  color: "common.white",
                  backgroundColor: "secondary.main",
                  "&:hover": {
                    backgroundColor: "secondary.main",
                  },
                }}
              >
                <LuFilter size={16} />
              </IconButton>
            )}
          </Stack> */}
        </Stack>
        {/* Top end */}
        {/* Table */}
        <Box
          id="table-filter-layout"
          sx={{
            width: "100%",
            height: `calc(100vh - ${heights.header} - ${heights.py} - ${heights.topList} - ${heights.pagination})`,
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: category.allowFilter
                ? `calc(100%${expandFilter ? ` - ${widthes.filter}` : ""})`
                : "100%",
              height: "100%",
              transition: "all 0.1s",
              transitionTimingFunction: "ease-out",
            }}
          >
            <TableApp
              toggleCleared={toggleClear}
              rowPerPage={paginationOption.limit}
              columns={columns}
              data={dataToShow}
              selectableRows={false}
              fixedHeader
              progressPending={loading}
              onRowClicked={handleRowClicked}
              fixedHeaderScrollHeight={`calc(100vh - ${HEADER_HEIGHT} - ${heights.py} - ${heights.topList} - ${heights.pagination})`}
            />
          </Box>
          {/* Filter */}
          {/* {category.allowFilter && (
            <Box
              sx={{
                height: "100%",
                width: expandFilter ? widthes.filter : "0px",
                transition: "all 0.1s",
                transitionTimingFunction: "ease-out",
                borderLeft: "2px solid",
                borderColor: expandFilter ? "primary.main" : "transparent",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                }}
              >
                {category.Filter && (
                  <category.Filter
                    loading={loading}
                    setCondition={setCondition}
                  />
                )}
              </Box>
            </Box>
          )} */}
        </Box>
        {/* Table end */}
        {/* Pagination */}
        <Box
          sx={{
            mt: "auto",
            borderTop: "1px dashed",
            borderColor: "divider",
            height: heights.pagination,
          }}
        >
          {paginationOption.totalRows > 0 && (
            <Tablepagination
              currentPage={paginationOption.page}
              onChangePage={(value) =>
                setPaginationOption({ ...paginationOption, page: value })
              }
              rowsPerPage={paginationOption.limit}
              rowCount={paginationOption.totalRows}
              onChangeRowsPerPage={handleRowPerPageChange}
              paginationRowsPerPageOptions={[20, 50, 100]}
            />
          )}
        </Box>
        {/* Pagination end */}
      </Box>
    </>
  );
}

export default TrashBase;
